import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// İkon komponentleri
const ExcelIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 2V8H20" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 16L10 18L16 12" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const WebIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M2 12H22" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const AIIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 14C8 14 9.5 16 12 16C14.5 16 16 14 16 14" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 9H9.01" stroke="#00FFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 9H15.01" stroke="#00FFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DataIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 8C16.9706 8 21 6.65685 21 5C21 3.34315 16.9706 2 12 2C7.02944 2 3 3.34315 3 5C3 6.65685 7.02944 8 12 8Z" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M21 12C21 13.66 17 15 12 15C7 15 3 13.66 3 12" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 5V19C3 20.66 7 22 12 22C17 22 21 20.66 21 19V5" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Yeni eklenen harcama takibi ikonu
const ExpenseIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 8L6 4H18L21 8" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M21 12V18C21 19.1046 20.1046 20 19 20H5C3.89543 20 3 19.1046 3 18V12" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6 12H18V16H6V12Z" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 16V12" stroke="#00FFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CaseStudiesSection = () => {
  const { t } = useTranslation();
  
  // References içeriğindeki veriler yerine caseStudies verilerini kullan
  const [activeCase, setActiveCase] = useState(0);

  // Vaka çalışmalarını i18n üzerinden al
  const caseStudies = t('caseStudies.cases', { returnObjects: true }) || [];
  
  // Veri boş gelebilir, kontrol et
  if (!caseStudies || caseStudies.length === 0) return null;

  // İkon gösterimini sağla
  const getIcon = (id) => {
    switch(id) {
      case 1: return <ExcelIcon />;
      case 2: return <AIIcon />;
      case 3: return <WebIcon />;
      case 4: return <DataIcon />;
      case 5: return <ExpenseIcon />; // Yeni ikon çağrısı
      default: return <ExcelIcon />;
    }
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl md:text-4xl font-bold mb-4 font-poppins">
          <span className="text-cyan-400">{t('caseStudies.titleHighlight')}</span> {t('caseStudies.title')}
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto font-roboto">{t('caseStudies.subtitle')}</p>
      </motion.div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sol taraf - Case study listesi */}
        <div className="lg:w-1/3">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <h3 className="text-xl font-semibold mb-4 text-cyan-400">{t('caseStudies.sectorLabel')}</h3>
            <ul>
              {caseStudies.map((caseItem, index) => (
                <motion.li 
                  key={caseItem.id}
                  className={`mb-2 p-4 rounded-lg cursor-pointer transition-all duration-300 flex items-center gap-3 ${
                    activeCase === index 
                      ? 'bg-gray-700 border-l-4 border-cyan-400' 
                      : 'hover:bg-gray-700'
                  }`}
                  onClick={() => setActiveCase(index)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <span className="text-2xl">{getIcon(caseItem.id)}</span>
                  <div>
                    <div className="font-medium">{caseItem.title}</div>
                    <div className="text-sm text-gray-400">{caseItem.sector}</div>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>

        {/* Sağ taraf - Detaylı vaka incelemesi */}
        <div className="lg:w-2/3 bg-gray-800 rounded-lg p-6 border border-gray-700">
          <motion.div
            key={caseStudies[activeCase].id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center mb-6">
              <span className="text-4xl mr-4">{getIcon(caseStudies[activeCase].id)}</span>
              <div>
                <h3 className="text-2xl font-bold text-cyan-400">{caseStudies[activeCase].title}</h3>
                <p className="text-gray-400">{caseStudies[activeCase].sector}</p>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-2">{t('caseStudies.problemLabel')}</h4>
              <p className="text-gray-300">{caseStudies[activeCase].problem}</p>
            </div>

            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-2">{t('caseStudies.solutionLabel')}</h4>
              <p className="text-gray-300">{caseStudies[activeCase].solution}</p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-2">{t('caseStudies.resultsLabel')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {caseStudies[activeCase].results.map((result, index) => (
                  <motion.div 
                    key={index} 
                    className="bg-gray-700 p-4 rounded-lg border-l-4 border-cyan-400"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    {result}
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div 
              className="mt-8 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <a 
                href="#contact" 
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-black py-2 px-6 rounded-lg font-medium hover:opacity-90 transition-all"
              >
                {t('caseStudies.ctaButton')}
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CaseStudiesSection;